import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Avatar, Alert } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  CheckCircle as ActiveIcon,
  HourglassEmpty as PendingIcon,
  Block as DisabledIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { UserListDto } from '../../types/admin';

const UserManagement: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const { token } = useAuth();

  const [users, setUsers] = useState<UserListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const loadUsers = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getUsers(
        {
          page: paginationModel.page + 1, // DataGrid uses 0-based, API uses 1-based
          pageSize: paginationModel.pageSize,
        },
        token
      );
      setUsers(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users. Please try again.');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const getStatusChip = (status: string) => {
    const config = {
      pending: {
        icon: <PendingIcon />,
        color: 'warning' as const,
        label: t('userStatus.pending'),
      },
      active: {
        icon: <ActiveIcon />,
        color: 'success' as const,
        label: t('userStatus.active'),
      },
      disabled: {
        icon: <DisabledIcon />,
        color: 'error' as const,
        label: t('userStatus.disabled'),
      },
    };
    const statusConfig = config[status as keyof typeof config];
    return (
      <Chip
        icon={statusConfig.icon}
        label={statusConfig.label}
        color={statusConfig.color}
        size="small"
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: t('columns.email'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fullName',
      headerName: t('columns.fullName'),
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: string | null | undefined) => value || t('notSet'),
    },
    {
      field: 'status',
      headerName: t('columns.status'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => getStatusChip(params.value),
    },
    {
      field: 'monitoredIdentityCount',
      headerName: t('columns.monitoredIdentities'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'isAdmin',
      headerName: t('columns.admin'),
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
              <AdminIcon sx={{ fontSize: 16 }} />
            </Avatar>
          </Box>
        ) : null,
    },
    {
      field: 'createdAt',
      headerName: t('columns.createdAt'),
      width: 150,
      valueFormatter: (value: string | null | undefined) =>
        value ? new Date(value).toLocaleDateString(i18n.language) : t('notSet'),
    },
  ];

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/${i18n.language}/admin/users/${params.row.id}`);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('userManagement.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('userManagement.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%', mt: 3 }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          rowCount={totalCount}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography color="text.secondary">
                  {t('userManagement.noUsers')}
                </Typography>
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default UserManagement;
